import { createReducer } from '@reduxjs/toolkit';
import type { SubscriptionState } from '../types/subscription';
import { logout } from '../types';
import {
  fetchCurrentSubscription,
  fetchInvoices,
  cancelSubscription,
  resetSubscriptionFetched,
  fetchOrganizationUsage,
  resetUsageFetched,
} from '../actions/subscription';
import { switchOrganization } from '../actions/organizations';

const initialState: SubscriptionState = {
  currentSubscription: null,
  isSubscriptionLoading: false,
  fetchedByOrg: {},
  invoices: [],
  isInvoicesLoading: false,
  isActionLoading: false,
  usage: null,
  isUsageLoading: false,
  usageFetchedByOrg: {},
};

export const subscriptionReducer = createReducer(initialState, (builder) => {
  builder
    // Fetch current subscription
    .addCase(fetchCurrentSubscription.pending, (state) => {
      state.isSubscriptionLoading = true;
    })
    .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
      state.isSubscriptionLoading = false;
      state.currentSubscription = action.payload.subscription;
      state.fetchedByOrg[String(action.payload.orgId)] = true;
    })
    .addCase(fetchCurrentSubscription.rejected, (state, action) => {
      state.isSubscriptionLoading = false;
      // Still mark as fetched to prevent infinite refetching on error
      const orgId = action.meta.arg;
      state.fetchedByOrg[String(orgId)] = true;
    })

    // Fetch invoices
    .addCase(fetchInvoices.pending, (state) => {
      state.isInvoicesLoading = true;
    })
    .addCase(fetchInvoices.fulfilled, (state, action) => {
      state.isInvoicesLoading = false;
      state.invoices = action.payload;
    })
    .addCase(fetchInvoices.rejected, (state) => {
      state.isInvoicesLoading = false;
    })

    // Cancel subscription
    .addCase(cancelSubscription.pending, (state) => {
      state.isActionLoading = true;
    })
    .addCase(cancelSubscription.fulfilled, (state) => {
      state.isActionLoading = false;
      if (state.currentSubscription) {
        state.currentSubscription.status = 'cancelled' as any;
      }
    })
    .addCase(cancelSubscription.rejected, (state) => {
      state.isActionLoading = false;
    })

    // Reset fetched flag for an org (after checkout success)
    .addCase(resetSubscriptionFetched, (state, action) => {
      delete state.fetchedByOrg[String(action.payload)];
      state.currentSubscription = null;
    })

    // Fetch organization usage
    .addCase(fetchOrganizationUsage.pending, (state) => {
      state.isUsageLoading = true;
    })
    .addCase(fetchOrganizationUsage.fulfilled, (state, action) => {
      state.isUsageLoading = false;
      state.usage = action.payload.usage;
      state.usageFetchedByOrg[String(action.payload.orgId)] = true;
    })
    .addCase(fetchOrganizationUsage.rejected, (state, action) => {
      state.isUsageLoading = false;
      // Still mark as fetched to prevent infinite refetching on error.
      const orgId = action.meta.arg;
      state.usageFetchedByOrg[String(orgId)] = true;
    })

    // Invalidate cached usage for an org (after a mutation that changes
    // member or channel count).
    .addCase(resetUsageFetched, (state, action) => {
      delete state.usageFetchedByOrg[String(action.payload)];
      state.usage = null;
    })

    // Clear on organization switch — force re-fetch for new org
    .addCase(switchOrganization, (state, action) => {
      state.currentSubscription = null;
      state.invoices = [];
      state.usage = null;
      // Delete fetched flags so ProtectedRoute triggers a re-fetch
      delete state.fetchedByOrg[String(action.payload)];
      delete state.usageFetchedByOrg[String(action.payload)];
    })

    // Logout — full reset
    .addCase(logout, () => initialState);
});
